import type { UiPluginName } from '@/views/processes/injectables/uiPlugins/pluginTypes';
import type { MaybeSlip } from '@/views/tickets/ticketsTypes';

/** JSON-safe recursive value type (good for serializing to processSlip) */
export type JSONPrimitive = string | number | boolean | null;
export type JSONValue = JSONPrimitive | JSONValue[] | { [key: string]: JSONValue };
export type JsonObject = { [key: string]: JSONValue };

/** Base values type for UI plugins. Accepts arbitrary key-values for now. */
export type UiPluginValues = JsonObject;

export type ProcessSlipEntity = {
  ticketId: string;
  version: number;
  processSlip: ProcessSlipObject;
  entryKey: 'uiPluginStates';
  entryValue: UiPluginStates;
};

/** Payload that a plugin sends as state */
export type UiPluginState<V extends UiPluginValues = UiPluginValues> = {
  instanceId?: UiPluginId;
  ready: boolean;
  naturalReady?: boolean;
  isOptional?: boolean;
  values?: V;
  forcedReady?: boolean | { by: string; at: string } | null;
};

export type UiPluginStateKeys = keyof UiPluginState;

/** Submission payload from a plugin action */
export interface SubmitSuccessPayload {
  ticketId: string;
  itemID?: string;
  data?: JSONValue;
}

// --- Id-Key: "<scopeOrProcessName>:<PluginName>:<index>" ----------------
// Matches keys like "alle-komponenten-all-components:EmailComponent:1"
export type UiPluginId = `${string}:${UiPluginName}:${number}`;

// The ProcessSlip uiPluginStates map (keys are dynamic ids)
export type UiPluginStates = Partial<Record<string, UiPluginState>>;

/** ProcessSlip handled as object */
export type ProcessSlipObject = JsonObject & {
  uiPluginStates?: UiPluginStates;
};

// ---------------- helpers ----------------

/** Narrow to plain object (not null, not array) */
function isPlainObject(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

/** Safely coerce MaybeSlip into a ProcessSlipObject */
export function toSlipObject(input: MaybeSlip): ProcessSlipObject {
  if (!input) return {};

  if (typeof input === 'string') {
    try {
      const parsed = JSON.parse(input);
      return isPlainObject(parsed) ? (parsed as ProcessSlipObject) : {};
    } catch {
      return {};
    }
  }

  if (isPlainObject(input)) {
    // shallow clone; keeps unknown extra keys but still JSON-safe by our usage
    return { ...(input as Record<string, unknown>) } as ProcessSlipObject;
  }

  // arrays or other types are not acceptable for slip object
  return {};
}
